import { useContext } from 'react'
import { FormikContext } from 'formik'
import { showContentModal } from '~/lib/modal'
import classnames from 'classnames/bind'
import { StoreContext } from '~/store'
import { StepperContext } from '~/components/ui/ViewStepper'
import { getOptions, formatReadableDate } from '~/utils/format'
import { getTaxiAndEta, getOptionText, getVoucherValue, hasTaxiAndEta, hasInformationTag, getPaymentDisplay } from '~/utils/data'
import { EditField, BookingSummaryField, BookingType, TripType, TripLocations, PartnerContact, MessageToDriver, PassengerInfo, SelectedCar, SelectedPayment, TravelerCount } from '~/components'
import PriceInfoModal from '~/components/ui/PriceInfoModal'
import * as common from '~/config/common.module.css'
import * as styles from './index.module.css'
import * as MotionElement from '~/components/ui/MotionElement'
import { useTranslation } from 'react-i18next'
import i18next from '~/i18n'

const cx = classnames.bind(styles)

// const formatEta = (eta, serviceType) => {
//   const name = serviceType === 'business' ? 'premium' : serviceType
//   if (eta?.length > 0) {
//     const minutes = eta.find(item => name === item.vehicleType)?.expectedWaitTime
//     if (minutes === undefined) return null
//     return i18next.t('info-available-in', { minutes })
//   }
// }


const formatAvailability = (availabilities, serviceType) => {
  const availability = availabilities.find(item => serviceType === item.serviceType)
  if (availability?.expectedWaitTime === undefined) return null
  // if (availability?.availableAt === 'DELAYED') return `Endast förbokning ${formatTime(new Date(availability.pickupTime))}`
  if (availability?.availableAt === 'DELAYED') return `Endast förbokning`
  return i18next.t('info-available-in', { minutes: availability?.expectedWaitTime })
}



export default function BookingSummary({ values, currentView, showTime = false, showHow = false, showPayment = false, afterLocations = null }) {
  const hasBooking = !isNaN(values?.bookingId)
  const inEditMode = hasBooking && values.bookingId > 0

  // In edit mode, always show all sections
  const displayShowTime = inEditMode || showTime
  const displayShowHow = inEditMode || showHow
  const displayShowPayment = inEditMode || showPayment

  const isLocationView = currentView === 'locations';
  const isTimeView = currentView === 'time';
  const isHowView = currentView === 'how';
  const isConfirmView = currentView === 'confirm';
  const isDoneView = currentView === 'done';

  // 'locations' is the first view, so 'time' is still ahead of the user there —
  // offering the edit toggle would jump them forward into a step they have not
  // reached yet. Also hidden on the time step itself, and once a booking exists.
  const canChangeTime = !hasBooking && !isTimeView && !isLocationView

  const openEditPhone = () => showContentModal((close) => (
    <EditField validate formValues={values} onFieldSave={(id, val) => setFieldValue?.(id, val, false)} fields={[{id:'name', title:t('placeholder-name'), validate:validateName}, {id:'phone', title:t('placeholder-phone'), validate:validatePhone}]} title={t('header-whos-travel')} onClose={close} />
  ))

  const openEditMessage = () => showContentModal((close) => (
    <EditField formValues={values} onFieldSave={(id, val) => setFieldValue?.(id, val, false)} fields={[{id:'generalComment', title:t('title-message-to-driver')}]} title={t('title-message-to-driver')} onClose={close} />
  ))

  const openEditReference = () => showContentModal((close) => (
    <EditField formValues={values} onFieldSave={(id, val) => setFieldValue?.(id, val, false)} fields={[{id:'accountReference', title:t('placeholder-reference')}]} title={t('placeholder-reference')} onClose={close} />
  ))

  const { t } = useTranslation()
  const { bookings, user } = useContext(StoreContext)
  const { prev } = useContext(StepperContext)
  const { setFieldValue } = useContext(FormikContext) || {}

  // In edit mode, use booking from form values (stable). Otherwise, look up from store
  const booking = inEditMode && values.booking ? values.booking : bookings?.find(item => item.id === values.bookingId)


  const taxiAndEta = booking ? getTaxiAndEta(booking) : ''
  const pickupTime = values.autoValues.pickupTime || values.pickupTime
  const time = pickupTime
    ? formatReadableDate(pickupTime)
    : formatReadableDate(new Date())

  const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice
  const serviceType = values.autoValues.serviceType !== null ? values.autoValues.serviceType : values.serviceType
  const actualServiceType = serviceType === 'estate' ? 'taxi' : serviceType;
  const availability = values.availability?.availabilities ? formatAvailability(values.availability.availabilities, actualServiceType) : false
  const price = values?.priceEstimate?.prices?.find(p =>
    p.serviceType === actualServiceType &&
    (values.selectedProviderId ? p.providerId === values.selectedProviderId : true)
  )

  // In edit mode, get price from booking if available
  const editModePrice = inEditMode && booking ? booking.price : null
  const displayPrice = inEditMode ? editModePrice : price
  const editModeIsFixedPrice = inEditMode && booking ? booking.price?.priceType === 'FIXED' : fixedPrice
  const showPrice = (inEditMode && editModePrice) || (!inEditMode && fixedPrice && price)
  const options = getOptions(values)

  // SelectedPayment props
  const { icon: paymentIcon, title: paymentTitle, subtitle: paymentSubtitle } = getPaymentDisplay(values)
  const isPaymentFixedPrice = inEditMode ? editModeIsFixedPrice : (fixedPrice && showPrice)
  const paymentPriceLabel = isPaymentFixedPrice ? t('label-fixed-price') : t('label-taxameter')
  const voucherPills = values.voucherObjects?.length > 0
    ? values.voucherObjects.map(v => `${i18next.t('title-voucher')} ${v.voucherCode} - ${getVoucherValue(v)}`)
    : []
  const company = values.isCompanyTrip &&  user?.companyConnections ? user?.companyConnections[0] : false
  const showReference = values?.company !== false || values?.account?.type === 'TSAB' ||  values?.account?.type === 'TCA';
  const needReference = company?.requireReference === true || (values?.account !== false && (values?.account?.type ==='TSAB' || values?.account?.type ==='TCA') && values?.account?.flags?.includes('reference'))

  // Check if fields are editable in edit mode
  const isNameEditable = inEditMode ? values.editableFields?.includes('traveler.name') : false
  const isPhoneEditable = inEditMode ? values.editableFields?.includes('traveler.phoneNumber') : false
  const isCommentEditable = inEditMode ? values.editableFields?.includes('generalComment') : false
  const isTravelerInfoEditable = isNameEditable || isPhoneEditable


  const partnerData = (values?.availability?.direct?.providerData?.providerId && (values?.availability?.direct?.providerData?.providerId !== "TSAB" || CONFIG.svea_module)) ?
    values?.availability?.direct?.providerData :
    (values?.availability?.prebook?.providerData?.providerId && (values?.availability?.prebook?.providerData?.providerId !== "TSAB" || CONFIG.svea_module)) ?
    values?.availability?.prebook?.providerData :
    null;

  const availabilityKey = values.pickupTime === '' ? 'direct' : 'prebook'
  const servicesByProvider = values.availability?.[availabilityKey]?.servicesByProvider
  const selectedPartnerData = values.selectedProviderId && Array.isArray(servicesByProvider) && servicesByProvider.length > 0
    && !(servicesByProvider.length === 1 && servicesByProvider[0].providerData?.providerId === 'TSAB')
    ? servicesByProvider.find(p => p.providerData?.providerId === values.selectedProviderId)?.providerData ?? null
    : null

  const openPriceInfo = showPrice
    ? () => showContentModal((close) => <PriceInfoModal price={displayPrice} onClose={close} />)
    : null

  const validatePhone = value => {
    let errorMessage;
    console.log("PhoneMatch: ", value, String(value).match(/^\+?\d+$/));
    if(!String(value).match(/^\+?\d+$/)) {
      errorMessage = 'Fältet får bara inehålla siffor';
    }
    if (value.length < 2) {
      errorMessage = 'Fältet måste innehålla minst 2 tecken';
    }
    return errorMessage;
  };

  const validateName = value => {
    let errorMessage;
    if (value.length < 2) {
      errorMessage = 'Fältet måste innehålla minst 2 tecken';
    }
    return errorMessage;
  };

  return (
    <>
      <MotionElement.div className={cx(common.contentFull, styles.alignStart)}>
        {hasBooking && <div className={styles.group}>
          <div className={styles.groupContent}>
            <div className={styles.groupContentColumn}>
              <div className={styles.groupContentMainColumn}>
                <div className={styles.label}>{t('label-booking-number')}</div>
              </div>
              <div className={common.title}>{values.bookingId}</div>
            </div>
          </div>
        </div>}

        <div>
          <BookingType
            time={time}
            subtitle={
              values.flightNumber
                ? t('info-flight-monitoring')
                : (values.autoValues.pickupTime || values.pickupTime)
                  ? t('label-prebooked')
                  : (/*(hasTaxiAndEta(booking) && taxiAndEta) || availability ||*/ t('label-direct-booking'))
            }
            flightNumber={values.flightNumber}
            checkedBaggage={values.checkedInBaggage}
            changeToggle={canChangeTime ? () => prev('time') : null}
          />
        </div>

        <div>
          <TripLocations
            departure={values.departure}
            destination={values.destination}
            stops={values.intermediateStopLocations}
          />
        </div>

        {afterLocations}

        {displayShowPayment &&
          <div>
            <SelectedPayment
              icon={paymentIcon}
              title={paymentTitle}
              subtitle={paymentSubtitle}
              priceLabel={paymentPriceLabel}
              price={isPaymentFixedPrice ? displayPrice : null}
              priceLoading={!inEditMode && values.priceIsLoading}
              voucherPills={voucherPills}
              onInfo={openPriceInfo}
            />
          </div>
        }

        {displayShowPayment && showReference && hasBooking && values.accountReference.trim() !== '' &&
          <div>
            <BookingSummaryField title={t('placeholder-reference')} value={(!values.accountReference || values.accountReference.trim() === '') ? t('validation-need-reference') : values.accountReference} icon="Reference"></BookingSummaryField>
          </div>
        }

        {displayShowHow &&
          <div>
            <SelectedCar
              serviceType={serviceType}
              options={options.map(getOptionText)}
              onEdit={!hasBooking && !isHowView ? () => prev('how') : null}
            />
          </div>
        }

        {(booking?.traveler?.travelerCount > 0 ||
          (!isTimeView && hasInformationTag(values, 'TRAVELER_COUNT_REQUIRED') && values.passengerCount > 0)) &&
          <div>
            <TravelerCount count={booking?.traveler?.travelerCount || values.passengerCount} />
          </div>
        }

        {selectedPartnerData && displayShowHow &&
          <div>
            <PartnerContact
              providerId={selectedPartnerData.providerId}
              name={selectedPartnerData.name}
              phone={selectedPartnerData.phoneNumber}
              displayPhone={selectedPartnerData.displayPhoneNumber}
              email={selectedPartnerData.email}
            />
          </div>
        }

        {company && displayShowHow &&
          <div>
            <TripType type="company" name={company.name} />
          </div>
        }

        {user && <>
          <div>
            <PassengerInfo
              name={values.name}
              phone={values.phone}
              onEdit={(!hasBooking || isTravelerInfoEditable) && !isDoneView ? openEditPhone : null}
            />
          </div>
        </>}

        <div>
          <MessageToDriver
            message={values?.generalComment || 'Hjälp föraren att hitta dig.'}
            onEdit={(!hasBooking || isCommentEditable) && !isDoneView ? openEditMessage : null}
          />
        </div>

        {partnerData &&
          <div>
            <PartnerContact
              providerId={partnerData.providerId}
              name={partnerData.name}
              phone={partnerData.phoneNumber}
              displayPhone={partnerData.displayPhoneNumber}
              email={partnerData.email}
            />
          </div>
        }

      </MotionElement.div>
    </>
  )
}
